import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import captureUnitId from '../utils/capture-unit-id';
import EnsureAuthenticated from '../containers/EnsureAuthenticated';
import UnitAddress from '../components/UnitAddress';
import ScheduleShowing from '../containers/ScheduleShowing';
import ScheduleShowingPublic from '../containers/ScheduleShowingPublic';
import ScheduleShowingExperienceGate from '../containers/ScheduleShowingExperienceGate';
import SelfShowingLayout from '../layouts/SelfShowing';
import EnsureTrusted from '../containers/EnsureTrusted';
import ErrorMessage from '../components/ErrorMessage';

/**
 * Build the unchanged control scheduling tree.
 * @param {object} params
 * @param {object} params.unit
 * @param {string} params.unitAddress
 * @param {object} params.market
 * @param {string|number} params.unitId
 * @param {object} params.routeProps
 * @param {boolean} params.error
 * @returns {JSX.Element}
 */
function renderControlTree({ unit, unitAddress, market, unitId, routeProps, error }) {
  return (
    <EnsureAuthenticated
      pathName="/login"
      redirectTo="schedule a showing"
      redirectFor={unitAddress}
      verificationRequired={['email', 'phone', 'first_name', 'last_name']}
    >
      {error ? (
        renderUnitLoadFailure(unitId)
      ) : (
        <EnsureTrusted unit={unit}>
          <SelfShowingLayout px={[20, 30, 60]} py={40}>
            <ScheduleShowing
              match={routeProps.match}
              location={routeProps.location}
              history={routeProps.history}
              unitAddress={unitAddress}
              unitId={unitId}
              market={market}
            />
          </SelfShowingLayout>
        </EnsureTrusted>
      )}
    </EnsureAuthenticated>
  );
}

/**
 * Build the treatment scheduling tree: public tour-time list first, no auth/trust.
 * @param {object} params
 * @param {object} params.unit
 * @param {string} params.unitAddress
 * @param {string|number} params.unitId
 * @param {object} params.routeProps
 * @param {boolean} params.error
 * @returns {JSX.Element}
 */
function renderTreatmentTree({ unit, unitAddress, unitId, routeProps, error }) {
  if (error) {
    return renderUnitLoadFailure(unitId);
  }

  return (
    <SelfShowingLayout px={[20, 30, 60]} py={40}>
      <ScheduleShowingPublic
        match={routeProps.match}
        location={routeProps.location}
        history={routeProps.history}
        unitAddress={unitAddress}
        unitId={unitId}
        unit={unit}
      />
    </SelfShowingLayout>
  );
}

/**
 * Unit/address or market failure for the selected experience.
 * @param {string|number} unitId
 * @returns {JSX.Element}
 */
function renderUnitLoadFailure(unitId) {
  return (
    <SelfShowingLayout px={[20, 30, 60]} py={40}>
      <ErrorMessage type="ERR_UNIT_NOT_AVAILABLE" unitId={{ unit_id: unitId }} />
    </SelfShowingLayout>
  );
}

/**
 * Keep Control and Treatment failure handling behind the experiment boundary.
 * @param {object} params
 * @returns {JSX.Element}
 */
export function renderScheduleShowingExperience({ unit, unitAddress, market, unitId, routeProps, error }) {
  return (
    <ScheduleShowingExperienceGate
      unitId={unitId}
      control={renderControlTree({ unit, unitAddress, market, unitId, routeProps, error })}
      treatment={renderTreatmentTree({ unit, unitAddress, unitId, routeProps, error })}
    />
  );
}

/* eslint-disable */
export default withRouter(
  class Route extends Component {
    render() {
      const unitId = captureUnitId(this.props);
      if (!unitId) {
        this.props.history.push('/homes');
      }

      return (
        <UnitAddress unitId={unitId}>
          {({ unit, unitAddress, market, error }) =>
            (!!unitAddress || error) &&
            renderScheduleShowingExperience({
              unit,
              unitAddress,
              market,
              unitId,
              routeProps: this.props,
              error,
            })
          }
        </UnitAddress>
      );
    }
  },
);
